import { useEffect, useState } from "react";
import {
  faCircleQuestion,
  faWifiSlash,
  faXmark
} from "@fortawesome/pro-regular-svg-icons";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { T } from "@repo/transifex";
import { useIsOnline } from "~/providers/store/auth";
import { Dialog } from "~/components/Dialog";
import { Button } from "~/components/Button";

export const OfflineModal = () => {
  const isOnline = useIsOnline();
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    if (isOnline) {
      setIsMinimized(false);
    }
  }, [isOnline]);

  if (isOnline || isMinimized) {
    return null;
  }

  return (
    <OfflineModalContent
      onMinimize={() => {
        setIsMinimized(true);
      }}
    />
  );
};

type OfflineModalContentProps = {
  onMinimize?: () => void;
};

export const OfflineModalContent = ({
  onMinimize
}: OfflineModalContentProps = {}) => {
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  return (
    <Dialog size="md" backdropVariant="transparent" open>
      <div className="flex flex-col gap-6 p-6">
        <div className="flex items-start justify-between">
          <div className="flex size-12 items-center justify-center rounded-md bg-danger-backdrop">
            <FontAwesomeIcon
              icon={faWifiSlash}
              className="text-xl text-danger-dark"
            />
          </div>
          <div className="flex items-center gap-3">
            <OfflineDurationBadge />
            {onMinimize ? (
              <button
                type="button"
                onClick={onMinimize}
                className="text-text-secondary hover:text-text-primary"
              >
                <FontAwesomeIcon icon={faXmark} className="size-5" />
              </button>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <h2 className="text-2xl font-bold">
            <T _str="No internet connection" />
          </h2>
          <p className="text-text-secondary">
            <T _str="The till can't reach Kanpla. Payments and new orders are paused until the connection is restored." />
          </p>
        </div>

        {/* TODO: Add back in when we have a way to retry the connection */}
        {/* <Button variant="primary" size="large" icon={faRefresh}>
          <T _str="Try again" />
        </Button> */}

        <Button
          variant="transparent"
          size="medium"
          icon={faCircleQuestion}
          onClick={() => {
            setIsHelpOpen((v) => !v);
          }}
          className="-mt-2 text-text-secondary"
        >
          <span className="flex items-center gap-2">
            {isHelpOpen ? <T _str="Hide help" /> : <T _str="Get help" />}
            <FontAwesomeIcon icon={isHelpOpen ? faChevronUp : faChevronDown} />
          </span>
        </Button>

        {isHelpOpen ? (
          <div className="flex flex-col border-t border-interface-neutral-2 pt-6">
            <p className="text-xs font-medium uppercase tracking-wide text-text-secondary">
              <T _str="Try these in order" />
            </p>
            <div className="mt-2 flex flex-col divide-y divide-interface-neutral-2">
              <HelpStep
                n={1}
                title={<T _str="Check the router" />}
                body={
                  <T _str="All lights should be solid. Unplug it for 10 seconds, then plug it back in." />
                }
              />
              <HelpStep
                n={2}
                title={<T _str="Restart this till" />}
                body={
                  <T _str="Power it off fully, wait 10 seconds, then turn it back on." />
                }
              />
              <HelpStep
                n={3}
                title={<T _str="Check other devices" />}
                body={
                  <T _str="If nothing in the canteen has internet, the whole network is down — not just this till." />
                }
              />
            </div>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
};

const OfflineDurationBadge = () => {
  const elapsedSeconds = useElapsedSeconds();

  return (
    <div className="flex items-center gap-2 text-sm text-text-secondary">
      <span className="size-2 rounded-full bg-danger-main" />
      <span>
        {formatOfflineDuration(elapsedSeconds)} <T _str="offline" />
      </span>
    </div>
  );
};

type HelpStepProps = {
  n: number;
  title: React.ReactNode;
  body: React.ReactNode;
};

const HelpStep = ({ n, title, body }: HelpStepProps) => {
  return (
    <div className="flex items-start gap-3 py-4 first:pt-2 last:pb-0">
      <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-interface-neutral-2 text-xs font-medium text-text-secondary">
        {n}
      </div>
      <div className="flex flex-col gap-0.5">
        <p className="font-semibold">{title}</p>
        <p className="text-sm text-text-secondary">{body}</p>
      </div>
    </div>
  );
};

const useElapsedSeconds = () => {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const start = Date.now();
    const interval = setInterval(() => {
      setSeconds(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  return seconds;
};

const formatOfflineDuration = (totalSeconds: number): string => {
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m ${seconds}s`;
};
